import React, { useState, useEffect } from "react";
import useInView from "../hooks/useInView";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import tr from "date-fns/locale/tr";

// Slider resimleri
const images = [
    {
        url: "/images/hero-1.jpg",
        title: "Cyprus Yacht Club ile Lüks Deneyim",
        subtitle:
            "Akdeniz'in en güzel koylarında unutulmaz bir yolculuğa çıkın",
    },
    {
        url: "/images/hero-2.jpg",
        title: "Kıbrıs'ın En İyi Yat Kiralama Servisi",
        subtitle: "Özel rotalarda muhteşem tatil deneyimi",
    },
    {
        url: "/images/hero-3.jpg",
        title: "Premium Yat Koleksiyonu",
        subtitle: "En lüks yatlarla Akdeniz'in tadını çıkarın",
    },
];

registerLocale("tr", tr);

function Hero() {
    const [sectionRef, isInView] = useInView({ threshold: 0.1 });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isContentVisible, setIsContentVisible] = useState(true);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const navigate = useNavigate();

    // Slider değişimi ve içerik animasyonu
    const changeSlide = (index) => {
        setIsContentVisible(false);

        setTimeout(() => {
            setCurrentImageIndex(index);
            setIsContentVisible(true);
        }, 400);
    };

    // Otomatik slider değişimi
    useEffect(() => {
        const timer = setTimeout(() => {
            const nextIndex =
                currentImageIndex === images.length - 1
                    ? 0
                    : currentImageIndex + 1;
            changeSlide(nextIndex);
        }, 5000);

        return () => clearTimeout(timer);
    }, [currentImageIndex]);

    const formatDate = (date) => {
        if (!date) return "";

        const days = [
            "Pazar",
            "Pazartesi",
            "Salı",
            "Çarşamba",
            "Perşembe",
            "Cuma",
            "Cumartesi",
        ];
        const months = [
            "Ocak",
            "Şubat",
            "Mart",
            "Nisan",
            "Mayıs",
            "Haziran",
            "Temmuz",
            "Ağustos",
            "Eylül",
            "Ekim",
            "Kasım",
            "Aralık",
        ];

        const day = days[date.getDay()];
        const month = months[date.getMonth()];

        return `${date.getDate()} ${month}, ${day}`;
    };

    // Yacht search functionality
    const handleYachtSearch = async (e) => {
        e.preventDefault();
        
        if (!startDate || !endDate) {
            alert("Lütfen başlangıç ve bitiş tarihlerini seçin.");
            return;
        }

        if (startDate >= endDate) {
            alert("Bitiş tarihi başlangıç tarihinden sonra olmalıdır.");
            return;
        }

        setIsSearching(true);

        try {
            // Format date as YYYY-MM-DDTHH:mm:ss without timezone conversion
            const formatLocalDateTime = (date, hours = 10) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const time = `${String(hours).padStart(2, '0')}:00:00`;
                return `${year}-${month}-${day}T${time}`;
            };

            // IMPORTANT: Backend has swapped field names - endDate is actually startDate and vice versa
            const searchData = {
                "endDate": formatLocalDateTime(startDate, 10),  // This is actually the START date (backend bug)
                "startDate": formatLocalDateTime(endDate, 18)   // This is actually the END date (backend bug)
            };

            const response = await fetch("/rest/Yacht/getAvailableYachts", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(searchData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const availableYachts = await response.json();
            console.log("Available yachts:", availableYachts);

            // Navigate to yachts screen with search results
            navigate("/yachts", {
                state: {
                    searchResults: availableYachts,
                    searchFilters: {
                        endDate: formatLocalDateTime(startDate, 10),
                        startDate: formatLocalDateTime(endDate, 18)
                    }
                }
            });

        } catch (error) {
            console.error("Yacht search error:", error);
            alert("Yat arama sırasında bir hata oluştu. Lütfen tekrar deneyin.");
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div className="relative h-screen overflow-hidden hero-section">
            {/* Custom DatePicker Styles */}
            <style>{`
                .custom-datepicker .react-datepicker {
                    border: none !important;
                    border-radius: 16px !important;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
                        0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
                    font-family: inherit !important;
                    z-index: 9999 !important;
                    backdrop-filter: blur(10px) !important;
                    background: rgba(255, 255, 255, 0.98) !important;
                }

                .custom-datepicker .react-datepicker__header {
                    background: transparent !important;
                    border: none !important;
                    padding: 20px 16px 16px !important;
                    border-radius: 16px 16px 0 0 !important;
                }

                .custom-datepicker .react-datepicker__current-month {
                    font-size: 18px !important;
                    font-weight: 600 !important;
                    color: #1f2937 !important;
                    margin-bottom: 12px !important;
                }

                .custom-datepicker .react-datepicker__navigation {
                    top: 22px !important;
                    width: 32px !important;
                    height: 32px !important;
                    border-radius: 8px !important;
                    transition: all 0.2s ease !important;
                }

                .custom-datepicker .react-datepicker__navigation:hover {
                    background: #f3f4f6 !important;
                }

                .custom-datepicker .react-datepicker__navigation-icon::before {
                    border-color: #6b7280 !important;
                    border-width: 2px 2px 0 0 !important;
                    width: 8px !important;
                    height: 8px !important;
                }

                .custom-datepicker .react-datepicker__day-names {
                    margin-bottom: 8px !important;
                }

                .custom-datepicker .react-datepicker__day-name {
                    color: #6b7280 !important;
                    font-weight: 500 !important;
                    font-size: 14px !important;
                    width: 40px !important;
                    height: 32px !important;
                    line-height: 32px !important;
                }

                .custom-datepicker .react-datepicker__month {
                    padding: 0 16px 16px !important;
                }

                .custom-datepicker .react-datepicker__week {
                    display: flex !important;
                    gap: 2px !important;
                    margin-bottom: 2px !important;
                }

                .custom-datepicker .react-datepicker__day {
                    width: 40px !important;
                    height: 40px !important;
                    line-height: 40px !important;
                    border-radius: 10px !important;
                    font-weight: 500 !important;
                    font-size: 14px !important;
                    color: #374151 !important;
                    transition: all 0.2s ease !important;
                    cursor: pointer !important;
                    position: relative !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }

                /* Past dates styling - gray and disabled */
                .custom-datepicker .react-datepicker__day--disabled {
                    color: #d1d5db !important;
                    background: transparent !important;
                    cursor: not-allowed !important;
                    opacity: 0.5 !important;
                }

                .custom-datepicker .react-datepicker__day--disabled:hover {
                    background: transparent !important;
                    color: #d1d5db !important;
                }

                .custom-datepicker
                    .react-datepicker__day:hover:not(
                        .react-datepicker__day--disabled
                    ):not(.react-datepicker__day--selected) {
                    background: #eff6ff !important;
                    color: #2563eb !important;
                }

                .custom-datepicker .react-datepicker__day--selected {
                    background: #3b82f6 !important;
                    color: white !important;
                    font-weight: 600 !important;
                }

                .custom-datepicker .react-datepicker__day--selected:hover {
                    background: #2563eb !important;
                }

                .custom-datepicker .react-datepicker__day--keyboard-selected {
                    background: #dbeafe !important;
                    color: #1d4ed8 !important;
                }

                .custom-datepicker .react-datepicker__day--in-range {
                    background: #eff6ff !important;
                    color: #2563eb !important;
                }

                .custom-datepicker .react-datepicker__day--in-selecting-range {
                    background: #dbeafe !important;
                    color: #1d4ed8 !important;
                }

                .custom-datepicker .react-datepicker__day--range-start,
                .custom-datepicker .react-datepicker__day--range-end {
                    background: #3b82f6 !important;
                    color: white !important;
                    font-weight: 600 !important;
                }

                .custom-datepicker .react-datepicker__day--today {
                    position: relative !important;
                }

                .custom-datepicker .react-datepicker__day--today::after {
                    content: "" !important;
                    position: absolute !important;
                    bottom: 4px !important;
                    left: 50% !important;
                    transform: translateX(-50%) !important;
                    width: 6px !important;
                    height: 6px !important;
                    border-radius: 50% !important;
                    background: #3b82f6 !important;
                }

                .custom-datepicker
                    .react-datepicker__day--today.react-datepicker__day--selected::after {
                    background: white !important;
                }

                .custom-datepicker .react-datepicker-popper {
                    z-index: 9999 !important;
                }

                .custom-datepicker .react-datepicker__triangle {
                    display: none !important;
                }
            `}</style>

            {/* Slider Background */}
            {images.map((image, index) => (
                <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                        currentImageIndex === index
                            ? "opacity-100"
                            : "opacity-0"
                    }`}
                >
                    <img
                        src={image.url}
                        alt={`Slide ${index + 1}`}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50"></div>
                </div>
            ))}

            {/* Slider Dots */}
            <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex items-center gap-3 bg-black/20 px-6 py-3 rounded-full backdrop-blur-sm border border-white/10">
                {images.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => changeSlide(index)}
                        className={`transition-all duration-300 relative ${
                            currentImageIndex === index
                                ? "w-12"
                                : "w-2 bg-white/40 hover:bg-white/60"
                        } h-2 rounded-full overflow-hidden`}
                        aria-label={`Go to slide ${index + 1}`}
                    >
                        <span className="absolute inset-0 bg-white/40" />
                        {currentImageIndex === index && (
                            <span
                                className="absolute inset-0 bg-white"
                                style={{
                                    animation: `progressDot 5s linear forwards`,
                                    transformOrigin: "left",
                                }}
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* Hero Content */}
            <div
                ref={sectionRef}
                className={`relative z-10 flex flex-col items-center justify-center h-full text-white px-4 fade-in-left ${
                    isInView ? "is-visible" : ""
                }`}
            >
                <div
                    className={`fade-content ${
                        isContentVisible ? "active" : ""
                    }`}
                >
                    <h1 className="text-5xl md:text-6xl font-bold text-center mb-6">
                        {images[currentImageIndex].title}
                    </h1>
                    <p className="text-xl md:text-2xl text-center mb-8 max-w-3xl mx-auto">
                        {images[currentImageIndex].subtitle}
                    </p>
                </div>

                {/* Search Form */}
                <div className="bg-white/25 p-8 rounded-2xl w-full max-w-4xl backdrop-blur-xl shadow-2xl border border-white/40 relative z-20 before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/30 before:to-white/10 before:rounded-2xl before:-z-10">
                    <form onSubmit={handleYachtSearch}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Date Pickers */}
                            <div className="custom-datepicker">
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    placeholderText="Başlangıç Tarihi"
                                    className="w-full rounded-xl py-4 px-5 border border-gray-200 hover:border-blue-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-500"
                                    dateFormat="dd MMMM, eeee"
                                    minDate={new Date()}
                                    maxDate={endDate}
                                    locale="tr"
                                    isClearable={true}
                                    showPopperArrow={false}
                                    monthsShown={1}
                                    fixedHeight
                                    calendarClassName="rounded-xl border-none shadow-lg"
                                    popperClassName="custom-datepicker"
                                    customInput={
                                        <input
                                            value={
                                                startDate
                                                    ? formatDate(startDate)
                                                    : ""
                                            }
                                            placeholder="Başlangıç Tarihi"
                                            className="w-full rounded-xl py-4 px-5 bg-white/30 backdrop-blur-sm border border-white/50 hover:border-white/70 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/60 text-gray-800 placeholder-gray-600"
                                        />
                                    }
                                />
                            </div>
                            <div className="custom-datepicker">
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    placeholderText="Bitiş Tarihi"
                                    className="w-full rounded-xl py-4 px-5 border border-gray-200 hover:border-blue-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-500"
                                    dateFormat="dd MMMM, eeee"
                                    locale="tr"
                                    isClearable={true}
                                    showPopperArrow={false}
                                    monthsShown={1}
                                    fixedHeight
                                    calendarClassName="rounded-xl border-none shadow-lg"
                                    popperClassName="custom-datepicker"
                                    customInput={
                                        <input
                                            value={
                                                endDate
                                                    ? formatDate(endDate)
                                                    : ""
                                            }
                                            placeholder="Bitiş Tarihi"
                                            className="w-full rounded-xl py-4 px-5 bg-white/30 backdrop-blur-sm border border-white/50 hover:border-white/70 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/60 text-gray-800 placeholder-gray-600"
                                        />
                                    }
                                />
                            </div>

                            {/* Search Button */}
                            <button
                                type="submit"
                                disabled={isSearching || !startDate || !endDate}
                                className={`premium-button text-white text-sm px-4 py-1.5 rounded-[12px] transition-all duration-300 flex items-center justify-center gap-2 ${
                                    isSearching || !startDate || !endDate 
                                        ? 'opacity-50 cursor-not-allowed' 
                                        : 'hover:scale-105'
                                }`}
                            >
                                {isSearching ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                        Aranıyor...
                                    </>
                                ) : (
                                    'Tekne Ara'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Hero;
